import type { SxProps, Theme } from '@mui/material';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { Fragment, type ReactNode } from 'react';
import { BackButton } from './back-button';

/** The corner buttons, for a container that renders the header itself. */
export type PageHeaderButtonProps = {
  /** Shows the back button in the top-left corner. */
  onBack?: () => void;
  /** Accessible name of the icon-only back button. */
  backLabel?: string;
  /** Renders a labelled back button instead of the icon-only one. */
  backText?: ReactNode;
  /** Buttons in the top-right corner. */
  actions?: ReactNode;
};

export type PageHeaderProps = PageHeaderButtonProps & {
  /** One subtitle, or several parts shown in a row separated by dots. */
  subtitle?: ReactNode | ReactNode[];
  sx?: SxProps<Theme>;
} & (
    { title: ReactNode; loading?: false } | { title?: ReactNode; loading: true }
  );

const contentSx = {
  maxWidth: { xs: '100%', md: '80%' },
  margin: '0 auto',
  alignItems: 'center',
};

// Inset of the corner buttons from the top and sides, in spacing units.
const buttonInset = { xs: 2.5, md: 3 };

// `top`/`left`/`right` take raw lengths in `sx`, not spacing units.
const cornerSx =
  (side: 'left' | 'right'): SxProps<Theme> =>
  (theme) => {
    const inset = {
      xs: theme.spacing(buttonInset.xs),
      md: theme.spacing(buttonInset.md),
    };
    return { position: 'absolute', top: inset, [side]: inset };
  };

const toSubtitleParts = (subtitle: ReactNode | ReactNode[]) =>
  (Array.isArray(subtitle) ? subtitle : [subtitle]).filter(
    (part) =>
      part !== null && part !== undefined && part !== false && part !== ''
  );

const Subtitle = ({ parts }: { parts: ReactNode[] }) => {
  return (
    <Stack
      direction="row"
      sx={{
        columnGap: { xs: 1, md: 2 },
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        maxWidth: { md: '710px' },
      }}
    >
      {parts.map((part, index) => (
        <Fragment key={index}>
          {index > 0 && (
            <Box
              aria-hidden
              sx={{
                width: 4,
                height: 4,
                flexShrink: 0,
                borderRadius: '50%',
                bgcolor: 'text.secondary',
              }}
            />
          )}
          <Typography
            variant="subtitle1"
            component={parts.length > 1 ? 'span' : 'p'}
            align="center"
            sx={{ color: 'text.secondary' }}
          >
            {part}
          </Typography>
        </Fragment>
      ))}
    </Stack>
  );
};

/**
 * Page title and subtitle, centered, with an optional back button and
 * actions in the top corners. Spans the width it is given and brings all of
 * its own spacing and gutters: render it at the top of the page, outside any
 * `Container`.
 */
export const PageHeader = ({
  title,
  subtitle,
  onBack,
  backLabel,
  backText,
  actions,
  loading,
  sx,
}: PageHeaderProps) => {
  const sxList = Array.isArray(sx) ? sx : [sx];
  const hasButtons = !!onBack || !!actions;
  const subtitleParts = toSubtitleParts(subtitle);

  return (
    <Box
      sx={[
        {
          position: 'relative',
          px: buttonInset,
          pt: { xs: hasButtons ? 9 : 5, md: 8 },
          pb: { xs: 4, md: 3 },
        },
        ...sxList,
      ]}
    >
      {onBack && (
        <Box sx={cornerSx('left')}>
          <BackButton onClick={onBack} text={backText} ariaLabel={backLabel} />
        </Box>
      )}
      {actions && <Box sx={cornerSx('right')}>{actions}</Box>}
      {loading ? (
        <Stack sx={contentSx}>
          <Skeleton
            animation="wave"
            height="76px"
            width="240px"
            sx={{ mt: '-1.25rem' }}
          />
          <Skeleton
            animation="wave"
            height="28px"
            width="180px"
            sx={{ mt: '-0.75rem' }}
          />
        </Stack>
      ) : (
        <Stack spacing={0.75} sx={contentSx}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              // The desktop title sits 8px lower than the mobile one.
              pt: { md: 1 },
              textWrap: 'balance',
              textAlign: 'center',
              '.MuiDialog-root &': {
                fontSize: '36px',
              },
            }}
          >
            {title}
          </Typography>
          {subtitleParts.length > 0 && <Subtitle parts={subtitleParts} />}
        </Stack>
      )}
    </Box>
  );
};
